import React, { Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Environment, Network, Store, RecordSource } from 'relay-runtime'
import { lazy as loadable } from '@loadable/component'
import type { LoadableComponent } from '@loadable/component'

import { EmbedContext } from '@newComponents/Embed'
import { EmbedBrowserRouter } from '@newComponents/EmbedBrowserRouter'

import { Provider as DataProvider, createNetworkLayer } from '@data/Provider'
import { I18nProvider } from '@data/i18n'

import { TamaguiProvider } from 'tamagui'
import tamaguiConfig from '../../config/tamagui.config'

import { HOST_NAME } from '@data/config/url'

import {
  transformCSSForShadowDOM,
  injectCSSIntoShadowRoot,
  injectPortalCSS,
  interceptHeadStyles,
} from './shadowDOMStyles'
import { EMBED_COMPONENT_CSS } from './embedComponentCSS'

// Import Tamagui extracted CSS as raw string for Shadow DOM injection
import tamaguiCSS from '../../config/tamagui.css?inline'

// Font-face declarations — imported normally so fonts load globally in <head>.
// @font-face is global: once loaded, fonts are available inside Shadow DOM too.
import './embedStyles.css'

// SDK-specific ErrorBoundary — avoids legacy Screen/TokensContext dependencies
class SDKErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  componentDidCatch(error: Error) {
    console.error('[Guild SDK]', error)
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: 16,
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            color: '#666',
          }}
        >
          Something went wrong.
        </div>
      )
    }
    return this.props.children
  }
}

// SDK-specific Suspense wrapper — avoids legacy SuspenseWrapperLoadingSpinner
const wrapWithSuspense = <T extends object>(Comp: LoadableComponent<T>) => {
  const SuspenseEmbed = (props: T) => (
    <Suspense fallback={null}>
      <Comp {...props} />
    </Suspense>
  )

  SuspenseEmbed.displayName = `SuspenseEmbed(${
    Comp.displayName || Comp.name || 'Component'
  })`
  return SuspenseEmbed
}

const GuildCardEmbed = wrapWithSuspense(
  loadable(() => import('./Guild/GuildCardEmbed'))
)

const GuildEventLatest = wrapWithSuspense(
  loadable(() => import('./Guild/GuildEventLatest'))
)

const GuildEventList = wrapWithSuspense(
  loadable(() => import('./Guild/GuildEventList'))
)

const GuildPresentationLatest = wrapWithSuspense(
  loadable(() => import('./Guild/GuildPresentationLatest'))
)

const GuildPresentationList = wrapWithSuspense(
  loadable(() => import('./Guild/GuildPresentationList'))
)

const UserCardEmbed = wrapWithSuspense(
  loadable(() => import('./User/UserCardEmbed'))
)

const UserEventLatest = wrapWithSuspense(
  loadable(() => import('./User/UserEventLatest'))
)

const UserEventList = wrapWithSuspense(
  loadable(() => import('./User/UserEventList'))
)

const UserPresentationLatest = wrapWithSuspense(
  loadable(() => import('./User/UserPresentationLatest'))
)

const UserPresentationList = wrapWithSuspense(
  loadable(() => import('./User/UserPresentationList'))
)

const EventCardEmbed = wrapWithSuspense(
  loadable(() => import('./Event/EventCardEmbed'))
)

const EventItemEmbed = wrapWithSuspense(
  loadable(() => import('./Event/EventItemEmbed'))
)

const EventTicketSelectionEmbed = wrapWithSuspense(
  loadable(() => import('./Event/EventTicketSelectionEmbed'))
)

const PresentationCardEmbed = wrapWithSuspense(
  loadable(() => import('./Presentation/PresentationCardEmbed'))
)

const PresentationItemEmbed = wrapWithSuspense(
  loadable(() => import('./Presentation/PresentationItemEmbed'))
)

const GRAPHQL_URL = `${HOST_NAME}/graphql`

// --- Theme configuration ---

type SDKTheme = 'light' | 'dark' | 'auto'

const sdkConfig: { theme: SDKTheme } = { theme: 'auto' }

export const configure = (config: { theme?: SDKTheme }) => {
  if (config.theme !== undefined) sdkConfig.theme = config.theme
}

const resolveTheme = (theme: SDKTheme): 'light' | 'dark' => {
  if (theme === 'auto') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  }
  return theme
}

const themeToClass = (theme: 'light' | 'dark'): string =>
  theme === 'dark' ? 't_dark' : 't_light'

let relayEnvironment: Environment | undefined

const renderToElement = (
  element: HTMLElement | null,
  render: () => JSX.Element,
  theme?: SDKTheme
): (() => void) => {
  if (!element) {
    console.warn('Could not find HTML element to render SDK')
    return () => undefined
  }

  // Create Shadow DOM for CSS isolation
  const shadowRoot = element.attachShadow({ mode: 'open' })

  // 1. Inject design system CSS (tokens, themes, base styles)
  const designSystemCSS = tamaguiConfig.getCSS()
  if (designSystemCSS) {
    injectCSSIntoShadowRoot(
      shadowRoot,
      transformCSSForShadowDOM(designSystemCSS)
    )
  }

  // 2. Inject extracted Tamagui component CSS
  // The ?inline import may resolve to a module object; extract the string.
  const tamaguiCSSString =
    typeof tamaguiCSS === 'string'
      ? tamaguiCSS
      : typeof (tamaguiCSS as any)?.default === 'string'
        ? (tamaguiCSS as any).default
        : undefined
  if (tamaguiCSSString) {
    injectCSSIntoShadowRoot(
      shadowRoot,
      transformCSSForShadowDOM(tamaguiCSSString)
    )
  }

  // 3. Inject companion CSS for newComponents used in embeds
  if (EMBED_COMPONENT_CSS) {
    injectCSSIntoShadowRoot(shadowRoot, EMBED_COMPONENT_CSS)
  }

  // 4. Inject design system CSS into document.head for portaled content (tooltips, popovers).
  //    Tamagui's Portal renders to document.body, outside the Shadow DOM. These portaled
  //    elements need access to CSS variable definitions (theme tokens) to render correctly.
  //    We inject both the design system CSS (raw token values like --c-grey0) AND the
  //    extracted Tamagui CSS (theme variable mappings like --grey0: var(--c-grey0)).
  const portalCSSSources = [designSystemCSS, tamaguiCSSString]
    .filter(Boolean)
    .join('\n')
  const cleanupPortalCSS = portalCSSSources
    ? injectPortalCSS(portalCSSSources)
    : () => undefined

  // 5. Intercept all style injection into document.head (Tamagui, RNW, webpack chunks)
  const cleanupInterceptor = interceptHeadStyles(shadowRoot)

  // 6. Resolve theme: per-embed > global config > auto (prefers-color-scheme)
  const effectiveTheme = theme ?? sdkConfig.theme
  const resolved = resolveTheme(effectiveTheme)
  const themeClass = themeToClass(resolved)

  element.classList.add(themeClass, 'font_body')

  // 7. Create render target inside shadow root
  const wrapper = document.createElement('div')
  wrapper.className = `${themeClass} font_body`
  shadowRoot.appendChild(wrapper)

  // 8. Listen for OS theme changes when in 'auto' mode.
  //    CSS class swaps on host/wrapper update CSS variables (Tamagui theme tokens),
  //    but TamaguiProvider's defaultTheme is NOT updated — useThemeName() will return
  //    the initial theme. This is fine since no embed components branch on useThemeName().
  let cleanupMediaListener: () => void = () => undefined

  if (effectiveTheme === 'auto') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark' : 'light'
      const newClass = themeToClass(newTheme)
      const oldClass = newTheme === 'dark' ? 't_light' : 't_dark'

      element.classList.remove(oldClass)
      element.classList.add(newClass)
      wrapper.classList.remove(oldClass)
      wrapper.classList.add(newClass)
    }
    mediaQuery.addEventListener('change', handleChange)
    cleanupMediaListener = () =>
      mediaQuery.removeEventListener('change', handleChange)
  }

  // 9. Create React root in Shadow DOM
  const root = createRoot(wrapper)

  // Cache the Relay Environment so everything in the SDK uses the same normalized cache
  if (!relayEnvironment) {
    relayEnvironment = new Environment({
      network: Network.create(
        createNetworkLayer(
          undefined,
          undefined,
          () => Promise.resolve(undefined),
          GRAPHQL_URL
        )
      ),
      store: new Store(new RecordSource(), {
        gcReleaseBufferSize: 50,
        queryCacheExpirationTime: 60 * 60 * 1000,
      }),
    })
  }

  root.render(
    <TamaguiProvider
      config={tamaguiConfig}
      disableInjectCSS
      defaultTheme={resolved}
    >
      <DataProvider relayEnvironment={relayEnvironment}>
        {/* Embed components render shared newComponents that use <Trans>/useLingui.
            Without this provider those hooks read a null Lingui context and throw,
            which SDKErrorBoundary then swallows into "Something went wrong."
            Mirrors the DataProvider > I18nProvider nesting in App.tsx. */}
        <I18nProvider>
          <EmbedContext.Provider value={true}>
            <SDKErrorBoundary>
              <EmbedBrowserRouter forceExternalNavigation={true}>
                {render()}
              </EmbedBrowserRouter>
            </SDKErrorBoundary>
          </EmbedContext.Provider>
        </I18nProvider>
      </DataProvider>
    </TamaguiProvider>
  )

  return () => {
    cleanupMediaListener()
    cleanupPortalCSS()
    cleanupInterceptor()
    root.unmount()
  }
}

export const renderGuildCard = (
  element: HTMLElement,
  guildSlug: string,
  options: { expand?: boolean; theme?: SDKTheme } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <GuildCardEmbed {...rest} slug={guildSlug} />,
    theme
  )
}

export const renderGuildEventLatest = (
  element: HTMLElement,
  guildSlug: string,
  options: {
    showOrganizer?: boolean
    expand?: boolean
    theme?: SDKTheme
  } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <GuildEventLatest {...rest} slug={guildSlug} />,
    theme
  )
}

export const renderGuildEventList = (
  element: HTMLElement,
  guildSlug: string,
  timeSelection: 'UPCOMING' | 'PAST',
  options: {
    itemGap?: number
    numberToDisplay?: number
    expand?: boolean
    itemsDisplayed?: (itemsDisplayed: number) => unknown
    hasMore?: (hasMore: boolean) => unknown
    theme?: SDKTheme
  } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => (
      <GuildEventList
        {...rest}
        slug={guildSlug}
        timeSelection={timeSelection}
      />
    ),
    theme
  )
}

export const renderGuildPresentationLatest = (
  element: HTMLElement,
  guildSlug: string,
  options: {
    expand?: boolean
    theme?: SDKTheme
  } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <GuildPresentationLatest {...rest} slug={guildSlug} />,
    theme
  )
}

export const renderGuildPresentationList = (
  element: HTMLElement,
  guildSlug: string,
  timeSelection: 'UPCOMING' | 'OTHER',
  options: {
    itemGap?: number
    numberToDisplay?: number
    expand?: boolean
    itemsDisplayed?: (itemsDisplayed: number) => unknown
    hasMore?: (hasMore: boolean) => unknown
    theme?: SDKTheme
  } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => (
      <GuildPresentationList
        {...rest}
        slug={guildSlug}
        timeSelection={timeSelection}
      />
    ),
    theme
  )
}

export const renderUserCard = (
  element: HTMLElement,
  userSlug: string,
  options: { expand?: boolean; theme?: SDKTheme } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <UserCardEmbed {...rest} slug={userSlug} />,
    theme
  )
}

export const renderUserEventLatest = (
  element: HTMLElement,
  userSlug: string,
  options: {
    showOrganizer?: boolean
    expand?: boolean
    theme?: SDKTheme
  } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <UserEventLatest {...rest} slug={userSlug} />,
    theme
  )
}

export const renderUserEventList = (
  element: HTMLElement,
  userSlug: string,
  timeSelection: 'UPCOMING' | 'PAST',
  options: {
    itemGap?: number
    numberToDisplay?: number
    expand?: boolean
    itemsDisplayed?: (itemsDisplayed: number) => unknown
    hasMore?: (hasMore: boolean) => unknown
    theme?: SDKTheme
  } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => (
      <UserEventList {...rest} slug={userSlug} timeSelection={timeSelection} />
    ),
    theme
  )
}

export const renderUserPresentationLatest = (
  element: HTMLElement,
  userSlug: string,
  options: {
    expand?: boolean
    theme?: SDKTheme
  } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <UserPresentationLatest {...rest} slug={userSlug} />,
    theme
  )
}

export const renderUserPresentationList = (
  element: HTMLElement,
  userSlug: string,
  timeSelection: 'UPCOMING' | 'OTHER',
  options: {
    itemGap?: number
    numberToDisplay?: number
    expand?: boolean
    itemsDisplayed?: (itemsDisplayed: number) => unknown
    hasMore?: (hasMore: boolean) => unknown
    theme?: SDKTheme
  } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => (
      <UserPresentationList
        {...rest}
        slug={userSlug}
        timeSelection={timeSelection}
      />
    ),
    theme
  )
}

export const renderEventCard = (
  element: HTMLElement,
  eventSlug: string,
  options: { expand?: boolean; theme?: SDKTheme } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <EventCardEmbed {...rest} slug={eventSlug} />,
    theme
  )
}

export const renderEventItem = (
  element: HTMLElement,
  eventSlug: string,
  options: { expand?: boolean; theme?: SDKTheme } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <EventItemEmbed {...rest} slug={eventSlug} />,
    theme
  )
}

export const renderEventTicketSelection = (
  element: HTMLElement,
  eventSlug: string,
  options: { theme?: SDKTheme } = {}
) => {
  const { theme } = options
  return renderToElement(
    element,
    () => <EventTicketSelectionEmbed slug={eventSlug} />,
    theme
  )
}

export const renderPresentationCard = (
  element: HTMLElement,
  presentationSlug: string,
  options: { expand?: boolean; theme?: SDKTheme } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <PresentationCardEmbed {...rest} slug={presentationSlug} />,
    theme
  )
}

export const renderPresentationItem = (
  element: HTMLElement,
  presentationSlug: string,
  options: { expand?: boolean; theme?: SDKTheme } = {}
) => {
  const { theme, ...rest } = options
  return renderToElement(
    element,
    () => <PresentationItemEmbed {...rest} slug={presentationSlug} />,
    theme
  )
}

export default {
  configure,
  renderGuildCard,
  renderGuildEventLatest,
  renderGuildEventList,
  renderGuildPresentationLatest,
  renderGuildPresentationList,
  renderUserCard,
  renderUserEventLatest,
  renderUserEventList,
  renderUserPresentationLatest,
  renderUserPresentationList,
  renderEventCard,
  renderEventItem,
  renderEventTicketSelection,
  renderPresentationCard,
  renderPresentationItem,
}
